import type { ElementType } from 'react';
import { useCallback, useContext } from 'react';
import {
  Redirect, Route, RouteComponentProps, useLocation,
} from 'react-router-dom';
import Loader from 'components/layouts/modal/Loader';
import PopUp from 'components/layouts/modal/PopUp';
import PopUpV2 from '_revamp/molecules/modal/popUp';
import ModalAdd from 'components/layouts/modal/AddBoard';
import { Hooks } from 'providers';
import { useSelector } from 'react-redux';
import Wrapper from 'components/layouts/Main';
// organization
import ModalAddStructureNode from 'components/organizations/structure-view/ModalForm';
import ModalUpdateStructureNode from 'components/organizations/structure-view/ModalFormUpdate';
// employee
import ModalAddEmployeeNode from 'components/organizations/employees/ModalForm';
import ModalUpdateEmployeeNode from 'components/organizations/employees/ModalFormUpdate';
import { Organization } from 'providers/Organization';
import { Matrix } from 'providers/Matrix';

import ModalConflict from 'components/matrix/modal/ConflictData';
import Maintenance from 'components/layouts/maintenance';

type GuestProps = {
  readonly component: ElementType;
  readonly header?: boolean;
  readonly bypass?: boolean;
  readonly protected?: boolean | string;
  readonly path: string;
  readonly exact?: boolean;
  readonly [key: string]: unknown;
};

interface GuestRootState {
  readonly loading?: {
    readonly loading?: boolean;
  };
}

type ModalSetter = (value: boolean) => void;

type MatrixContextValue = {
  readonly modalAudit?: boolean;
  readonly modalConflict?: boolean;
  readonly modalSummary?: boolean;
  readonly setModalAudit: ModalSetter;
  readonly setModalSummary: ModalSetter;
  readonly setModalConflict: ModalSetter;
};

type HooksContextValue = {
  readonly modal?: boolean;
  readonly modalAdd?: boolean;
  readonly modalDelete?: boolean;
  readonly setModal: ModalSetter;
  readonly setModalDelete: ModalSetter;
  readonly setModalAdd: ModalSetter;
};

type OrganizationContextValue = {
  readonly employeeModal?: boolean;
  readonly modal?: boolean;
  readonly modalAddEmployee?: boolean;
  readonly modalUpdate?: boolean;
  readonly setModal: ModalSetter;
  readonly setModalUpdate: ModalSetter;
  readonly setEmployeeModal: ModalSetter;
  readonly setModalAddEmployee: ModalSetter;
};

function Guest({
  component: Component,
  header = false,
  bypass = false,
  protected: routeProtection = false,
  ...rest
}: GuestProps) {
  const token = localStorage.getItem('token');
  void routeProtection;
  const location = useLocation();
  const {
    modalAudit, modalConflict, modalSummary, setModalAudit, setModalSummary, setModalConflict,
  } = useContext(Matrix) as MatrixContextValue;
  const {
    modal, modalAdd, modalDelete, setModal, setModalDelete, setModalAdd,
  } = useContext(Hooks) as HooksContextValue;
  const {
    employeeModal,
    modal: organizationModal,
    modalAddEmployee,
    modalUpdate,
    setModal: setOrganizationModal,
    setModalUpdate,
    setEmployeeModal,
    setModalAddEmployee,
  } = useContext(Organization) as OrganizationContextValue;
  const { loading } = useSelector((state: GuestRootState) => ({
    loading: state.loading?.loading,
  }));

  const hasOpenModal = Boolean(
    modal
      || modalAdd
      || modalAudit
      || modalConflict
      || modalDelete
      || modalSummary
      || organizationModal
      || modalAddEmployee
      || modalUpdate
      || employeeModal,
  );

  const closeAll = useCallback(() => {
    setModalAudit(false);
    setModalSummary(false);
    setEmployeeModal(false);
    setModalAddEmployee(false);
    setModal(false);
    setModalUpdate(false);
    setOrganizationModal(false);
    setModalDelete(false);
    setModalAdd(false);
    setModalConflict(false);
  }, [
    setEmployeeModal,
    setModal,
    setOrganizationModal,
    setModalAdd,
    setModalAddEmployee,
    setModalAudit,
    setModalConflict,
    setModalDelete,
    setModalSummary,
    setModalUpdate,
  ]);

  if (
    ((process.env.REACT_APP_LIST_MAINTENANCE.length > 0
    && process.env.REACT_APP_LIST_MAINTENANCE.split(', ').includes(rest.path)) || process.env.REACT_APP_MAINTENANCE_IS_ACTIVE === 'true')
  ) {
    return (
      <Route
        {...rest}
        render={() => (
          <Maintenance />
        )}
      />
    );
  }
  if (token !== null && !bypass) {
    if (localStorage.getItem('admin')) {
      return <Redirect to="/back-office/dashboard?bo=company" />;
    }
    return <Redirect to="/board" />;
  }

  return (
    <Route
      {...rest}
      location={location}
      render={(props: RouteComponentProps) => (
        <>
          {loading && <Loader />}
          {header ? (
            <Wrapper>
              <Component {...props} {...rest} data="null" />
            </Wrapper>
          ) : (
            <Component {...props} {...rest} />
          )}

          <PopUp />
          <PopUpV2 />
          <ModalAdd />

          <ModalAddStructureNode title="Add Structure Node" />
          <ModalUpdateStructureNode title="Edit Structure Node" />
          <ModalAddEmployeeNode title="Add Employee Node" />
          <ModalUpdateEmployeeNode title="Edit Employee Node" />
          <ModalConflict />
          {hasOpenModal && (
            <button
              type="button"
              onClick={closeAll}
              aria-label="Close modal backdrop"
              className="modal-backdrop fade show"
              style={{
                border: 0,
                padding: 0,
                transition: 'all 0.5s ease',
              }}
            />
          )}
        </>
      )}
    />
  );
}

export default Guest;
